/**
 * Shepherd auto-tour rules per signed-in user:
 * - Each tour id may auto-start **at most once per login** (`toursAutoStartedThisLogin`).
 * - Each tour id may auto-start **at most 2 times total** across logins (`tourAutoStartTotalCounts`).
 *   If the user never hits a page that triggers a tour on login 1 or 2, that tour can still auto-start
 *   on login 3+ until its total reaches 2 (carry forward).
 * - `recordSuccessfulLoginForShepherdTours()` clears only the per-login map so the next login can
 *   show eligible tours again (one each), until totals reach 2.
 * - User dismiss (`cancel`, not app-driven cancel): tour id is stored in `skippedTourIds` and never
 *   auto-starts again. Explore main + continuation share skip state.
 */
import { getStoredAuthToken, getStoredUserId } from '../../utils/local-auth';
import {
  EXPLORE_ACTIVITY_CONTINUATION_TOUR,
  EXPLORE_ACTIVITY_TOUR,
} from './tours/explore-activity.tour';

const STORAGE_KEY_PREFIX = 'app_tour_login_state';

/** Legacy visit-count keys (pre login-based); cleared by QA reset. */
const LEGACY_VISIT_PREFIX = 'app_tour_area_visit_counts';

const MAX_AUTO_STARTS_PER_TOUR = 2;

function hashStringForStorageKey(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h) ^ input.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

export function getTourLoginStateStorageKey(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const uid = getStoredUserId()?.trim();
  if (uid) {
    return `${STORAGE_KEY_PREFIX}_u_${uid}`;
  }
  const token = getStoredAuthToken();
  if (token) {
    return `${STORAGE_KEY_PREFIX}_t_${hashStringForStorageKey(token)}`;
  }
  return null;
}

export interface TourLoginState {
  skippedTourIds: string[];
  /** Tour ids already auto-started during the current login session (cleared on next login). */
  toursAutoStartedThisLogin: Record<string, boolean>;
  /** Total times each tour was auto-started across all logins (capped at MAX_AUTO_STARTS_PER_TOUR). */
  tourAutoStartTotalCounts: Record<string, number>;
}

function defaultState(): TourLoginState {
  return {
    skippedTourIds: [],
    toursAutoStartedThisLogin: {},
    tourAutoStartTotalCounts: {},
  };
}

function parseCountsObject(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== 'object') {
    return out;
  }
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) {
      out[k] = Math.min(MAX_AUTO_STARTS_PER_TOUR, Math.floor(n));
    }
  }
  return out;
}

function parseState(raw: string | null): TourLoginState {
  const base = defaultState();
  if (!raw) {
    return base;
  }
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (Array.isArray(parsed['skippedTourIds'])) {
      base.skippedTourIds = (parsed['skippedTourIds'] as unknown[]).filter((x): x is string => typeof x === 'string');
    }
    if (parsed['toursAutoStartedThisLogin'] && typeof parsed['toursAutoStartedThisLogin'] === 'object') {
      base.toursAutoStartedThisLogin = { ...(parsed['toursAutoStartedThisLogin'] as Record<string, boolean>) };
    }
    if (parsed['tourAutoStartTotalCounts'] && typeof parsed['tourAutoStartTotalCounts'] === 'object') {
      base.tourAutoStartTotalCounts = parseCountsObject(parsed['tourAutoStartTotalCounts']);
    }
    return base;
  } catch {
    return base;
  }
}

function readState(): TourLoginState {
  if (typeof localStorage === 'undefined') {
    return defaultState();
  }
  const key = getTourLoginStateStorageKey();
  if (!key) {
    return defaultState();
  }
  return parseState(localStorage.getItem(key));
}

function writeState(state: TourLoginState): void {
  const key = getTourLoginStateStorageKey();
  if (!key) {
    return;
  }
  localStorage.setItem(key, JSON.stringify(state));
}

/** Call once after every successful sign-in / sign-up that stores `token` (+ user scope). */
export function recordSuccessfulLoginForShepherdTours(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (!getTourLoginStateStorageKey()) {
    return;
  }
  const s = readState();
  s.toursAutoStartedThisLogin = {};
  writeState(s);
}

export function isShepherdTourPermanentlySkipped(tourId: string): boolean {
  return readState().skippedTourIds.includes(tourId);
}

export function markShepherdTourPermanentlySkipped(tourId: string): void {
  const key = getTourLoginStateStorageKey();
  if (!key) {
    return;
  }
  const s = readState();
  const add = (id: string): void => {
    if (!s.skippedTourIds.includes(id)) {
      s.skippedTourIds.push(id);
    }
  };
  add(tourId);
  if (tourId === EXPLORE_ACTIVITY_TOUR || tourId === EXPLORE_ACTIVITY_CONTINUATION_TOUR) {
    add(EXPLORE_ACTIVITY_TOUR);
    add(EXPLORE_ACTIVITY_CONTINUATION_TOUR);
  }
  writeState(s);
}

export function canAutoStartShepherdTour(tourId: string): boolean {
  if (isShepherdTourPermanentlySkipped(tourId)) {
    return false;
  }
  const s = readState();
  const total = s.tourAutoStartTotalCounts[tourId] ?? 0;
  if (total >= MAX_AUTO_STARTS_PER_TOUR) {
    return false;
  }
  if (s.toursAutoStartedThisLogin[tourId]) {
    return false;
  }
  return true;
}

/** Mark tour as auto-started for this login and bump lifetime auto-start count (max 2 per tour id). */
export function markShepherdTourAutoStartedThisLogin(tourId: string): void {
  const key = getTourLoginStateStorageKey();
  if (!key) {
    return;
  }
  const s = readState();
  s.toursAutoStartedThisLogin[tourId] = true;
  const cur = s.tourAutoStartTotalCounts[tourId] ?? 0;
  s.tourAutoStartTotalCounts[tourId] = Math.min(MAX_AUTO_STARTS_PER_TOUR, cur + 1);
  writeState(s);
}

/** QA: remove all per-user login state blobs. */
export function clearAllTourLoginStateStorageForTesting(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k) {
      continue;
    }
    if (k === STORAGE_KEY_PREFIX || k.startsWith(`${STORAGE_KEY_PREFIX}_`)) {
      toRemove.push(k);
    }
  }
  for (const k of toRemove) {
    localStorage.removeItem(k);
  }
}

/** QA: legacy visit-count keys. */
export function clearLegacyTourVisitStorageForTesting(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k) {
      continue;
    }
    if (k === LEGACY_VISIT_PREFIX || k.startsWith(`${LEGACY_VISIT_PREFIX}_`)) {
      toRemove.push(k);
    }
  }
  for (const k of toRemove) {
    localStorage.removeItem(k);
  }
}

export function clearAllShepherdTourPersistenceForTesting(): void {
  clearLegacyTourVisitStorageForTesting();
  clearAllTourLoginStateStorageForTesting();
}
